import { useEffect, useRef } from 'react';

interface Props {
  /** Latest RMS level (0..1) — gets pushed into a ring buffer on each render. */
  level: number;
  /** Whether we're actively recording; controls animation speed/color. */
  active: boolean;
  /** Total bars in the waveform. */
  bars?: number;
  className?: string;
}

/**
 * Canvas-based waveform. Keeps a ring of the last N RMS levels and redraws on RAF.
 * Bars fade toward the center with a symmetric mirror, evoking a live oscilloscope.
 */
export function Waveform({ level, active, bars = 48, className }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ringRef = useRef<number[]>(Array(bars).fill(0));
  const rafRef = useRef<number | null>(null);
  const levelRef = useRef(level);
  const activeRef = useRef(active);

  useEffect(() => {
    levelRef.current = level;
  }, [level]);
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let lastTs = 0;
    const tick = (ts: number) => {
      if (ts - lastTs > 60) {
        lastTs = ts;
        const ring = ringRef.current;
        // simulate gentle decay if not recording, keeps baseline alive
        const incoming = activeRef.current
          ? levelRef.current
          : Math.max(0.03, ring[ring.length - 1] * 0.85);
        ring.shift();
        ring.push(incoming);
      }
      draw(ctx, canvas, ringRef.current, activeRef.current);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const { width, height } = canvas.getBoundingClientRect();
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      const ctx = canvas.getContext('2d');
      ctx?.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  return <canvas ref={canvasRef} className={className} />;
}

function draw(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  ring: number[],
  active: boolean
): void {
  const { width: w, height: h } = canvas.getBoundingClientRect();
  ctx.clearRect(0, 0, w, h);

  const cx = w / 2;
  const cy = h / 2;
  const n = ring.length;
  const gap = 3;
  const barW = Math.max(2, (w - gap * (n - 1)) / n);

  const styles = getComputedStyle(document.documentElement);
  const accent = styles.getPropertyValue('--accent').trim() || '99 102 241';
  const accent2 = styles.getPropertyValue('--accent-2').trim() || '236 72 153';

  for (let i = 0; i < n; i++) {
    const v = Math.max(0.03, Math.min(1, ring[i]));
    const h2 = v * h * 0.9;
    const x = i * (barW + gap);
    const distance = Math.abs(i - (n - 1) / 2) / ((n - 1) / 2);
    const alpha = active ? 1 - distance * 0.5 : 0.4 - distance * 0.2;
    const color = i / n < 0.5 ? accent : accent2;
    ctx.fillStyle = `rgb(${color} / ${alpha})`;
    const y = cy - h2 / 2;
    const r = barW / 2;
    roundRect(ctx, x, y, barW, h2, r);
  }

  // center line
  ctx.strokeStyle = `rgb(${accent} / ${active ? 0.25 : 0.1})`;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, cy);
  ctx.lineTo(w, cy);
  ctx.stroke();
  void cx;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
  ctx.fill();
}
